import { initialize } from 'store/create';
import { initializeGoogle } from 'utils/search/autocomplete';
import { saveBatchMeta, SAVE_BATCH_META_SUCCESS } from 'store/modules/user';
import {
	captureAllClickIds,
	buildMetaEntries as buildMetaEntriesFromCapture,
} from '@sparefoot/tracking-client';

const MAX_RETRIES = 10;
const RETRY_DELAY_MS = 300;
const STORAGE_KEY = 'ssdr_tracking_meta_sent';

/**
 * Get sent keys from sessionStorage for the current visitId.
 * Returns empty Set if visitId changed or no data exists.
 */
function getSentKeysForCurrentVisit(visitId) {
	try {
		if (typeof sessionStorage === 'undefined') {
			return new Set();
		}

		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) return new Set();

		const { vid, keys } = JSON.parse(stored);
		if (vid !== visitId) return new Set();

		return new Set(keys);
	} catch {
		return new Set();
	}
}

/**
 * Mark entries as sent for this visit after confirmed success.
 * Persists to sessionStorage so state survives page reloads within the session.
 */
function markEntriesAsSent(visitId, entries) {
	try {
		if (typeof sessionStorage === 'undefined') return;

		const existingKeys = getSentKeysForCurrentVisit(visitId);
		entries.forEach(({ key }) => existingKeys.add(key));

		sessionStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({
				vid: visitId,
				keys: Array.from(existingKeys),
			}),
		);
	} catch {
		// sessionStorage may be full or disabled - fail silently
	}
}

/**
 * Build array of meta entries that still need to be sent for this visit.
 * Uses shared library for capture, per-visit sessionStorage dedup.
 * @param {Object} user - Current user state
 * @returns {{ entries: Array, meta: Object, gcidValue: string|null }}
 */
function buildMetaEntries(user) {
	const captured = captureAllClickIds();
	const alreadySent = getSentKeysForCurrentVisit(user.visitId);
	const entries = buildMetaEntriesFromCapture(captured, alreadySent);

	// meta carries gcid for Redux state update
	const meta = {};
	if (captured.google_client_id) meta.gcid = captured.google_client_id;

	return { entries, meta, gcidValue: captured.google_client_id };
}

/**
 * Send meta entries and mark as sent.
 * Only marks entries as sent after the request confirms success -
 * a failed request leaves the keys unmarked so a later attempt
 * within the same visit will retry them.
 */
function sendMetaEntries(dispatch, visitId, entries, meta) {
	if (entries.length === 0) return;

	dispatch(saveBatchMeta(entries, meta))
		.then((result) => {
			if (result?.type === SAVE_BATCH_META_SUCCESS) {
				markEntriesAsSent(visitId, entries);
			}
		})
		.catch(() => {
			// A failed request leaves the keys unmarked so a later attempt
			// within the same visit will retry them - nothing else to do here.
		});
}

/**
 * Capture and save tracking meta with unified batching.
 * Supports all click IDs: google_client_id, gclid, gbraid, wbraid, fbclid, fbc, fbp, msclkid
 * Strategy:
 * - If gcid IS available: send immediately (batched with all other click IDs)
 * - If gcid is NOT available: retry until found or max retries, then send what we have
 */
function captureTrackingMeta(dispatch, getState) {
	let retries = 0;
	let hasSentBatch = false;

	function attemptCapture() {
		if (hasSentBatch) return;

		try {
			const { user } = getState();
			const { entries, meta, gcidValue } = buildMetaEntries(user);

			// If gcid is available, send immediately
			if (gcidValue) {
				hasSentBatch = true;
				sendMetaEntries(dispatch, user.visitId, entries, meta);
				return;
			}

			// gcid not available yet - retry or send what we have
			if (retries < MAX_RETRIES) {
				retries += 1;
				setTimeout(attemptCapture, RETRY_DELAY_MS);
			} else {
				// Max retries reached, send what we have (likely just other click IDs)
				hasSentBatch = true;
				sendMetaEntries(dispatch, user.visitId, entries, meta);
			}
		} catch {
			// Tracking capture must never block app bootstrap (Google Places,
			// smoothscroll polyfill, SSDC/INITIALIZE) - fail silently.
			hasSentBatch = true;
		}
	}

	attemptCapture();
}

const initializeApp = ({ dispatch, getState }) => {
	const { app } = getState();

	captureTrackingMeta(dispatch, getState);

	initializeGoogle({
		key: app.googleMapsKey,
	});

	dispatch(initialize());
};

export default initializeApp;
